import type { Command } from '@o2d/history';
import type { BoardState } from '../state/board-state.js';
import { isLine } from '../../features/line/line.types.js';
import type { LineObject, LineAnchor } from '../../features/line/line.interface.js';

export interface Move {
  readonly id: string;
  readonly from: { x: number; y: number };
  readonly to: { x: number; y: number };
}

/** The two anchors a line move may drop, remembered so revert can restore them. */
interface DroppedAnchors {
  readonly startAnchor?: LineAnchor;
  readonly endAnchor?: LineAnchor;
}

/**
 * Apply a translation to one or many objects. Both `from` and `to` are
 * captured up front (typically by the dragstart/dragend handlers on the
 * board) so revert can restore the original positions without further
 * lookups.
 *
 * Anchor detach rule (bugfix): a whole-line move repositions the line
 * independently, so on apply the line LETS GO of any anchor whose target is
 * NOT part of this same move. An anchor whose target co-moves (e.g. the box and
 * its connector dragged together as a selection) is KEPT so the pair stays
 * rigid. Dropped anchors are remembered per line and restored on revert, so
 * undo brings the connection back.
 */
export class MoveObjectsCommand implements Command<BoardState> {
  readonly label: string;
  /** Every id moved by this command — decides anchor detach vs. keep. */
  readonly #movedIds: ReadonlySet<string>;
  /** Anchors dropped on apply (target not co-moving), keyed by line id. */
  readonly #dropped = new Map<string, DroppedAnchors>();

  constructor(private readonly _moves: ReadonlyArray<Move>) {
    this.label = _moves.length === 1 ? 'Objekt verschieben' : `${_moves.length} Objekte verschieben`;
    this.#movedIds = new Set(_moves.map((m) => m.id));
  }

  get moves(): ReadonlyArray<Move> {
    return this._moves;
  }

  apply(state: BoardState): void {
    for (const m of this._moves) {
      this.#translate(state, m.id, m.to.x - m.from.x, m.to.y - m.from.y, m.to, true);
    }
  }

  revert(state: BoardState): void {
    for (const m of this._moves) {
      this.#translate(state, m.id, m.from.x - m.to.x, m.from.y - m.to.y, m.from, false);
    }
  }

  #translate(
    state: BoardState,
    id: string,
    dx: number,
    dy: number,
    origin: { x: number; y: number },
    isApply: boolean,
  ): void {
    const obj = state.getObject(id);
    if (!obj) return;

    if (!isLine(obj)) {
      state.updateObject({ ...obj, x: origin.x, y: origin.y });
      return;
    }

    const line = obj as LineObject;

    // An anchored endpoint whose target co-moves in THIS command is DERIVED from
    // the target, not translated here: AnchorSyncController's commit path re-derives
    // it from the moved target box (triggered by the target's object-updated event).
    // Translating it rigidly as well would move it twice — the group-move-with-
    // anchored-line bug. So a "kept" anchor's endpoint stays put; only free/detached
    // endpoints and the curve controls translate rigidly. The result is order-
    // independent: whichever of line/target moves first, the endpoint ends up moved
    // by exactly one delta.
    const keepStart = !!line.startAnchor && this.#movedIds.has(line.startAnchor.objectId);
    const keepEnd = !!line.endAnchor && this.#movedIds.has(line.endAnchor.objectId);
    const moved = {
      x: origin.x, y: origin.y,
      x1: keepStart ? line.x1 : line.x1 + dx,
      y1: keepStart ? line.y1 : line.y1 + dy,
      x2: keepEnd ? line.x2 : line.x2 + dx,
      y2: keepEnd ? line.y2 : line.y2 + dy,
      ...(line.curveControls
        ? { curveControls: line.curveControls.map((c) => ({ x: c.x + dx, y: c.y + dy })) }
        : {}),
    };

    if (!isApply) {
      // Revert: translate back and restore any anchors dropped on apply.
      state.updateObject({ ...line, ...moved, ...(this.#dropped.get(id) ?? {}) });
      return;
    }

    // Apply: keep an anchor only if its target co-moves in this same command;
    // otherwise detach it (and remember it for revert).
    const dropStart = !!line.startAnchor && !keepStart;
    const dropEnd = !!line.endAnchor && !keepEnd;
    if ((dropStart || dropEnd) && !this.#dropped.has(id)) {
      this.#dropped.set(id, {
        ...(dropStart ? { startAnchor: line.startAnchor } : {}),
        ...(dropEnd ? { endAnchor: line.endAnchor } : {}),
      });
    }

    const { startAnchor, endAnchor, ...bare } = line;
    state.updateObject({
      ...bare,
      ...moved,
      ...(startAnchor && !dropStart ? { startAnchor } : {}),
      ...(endAnchor && !dropEnd ? { endAnchor } : {}),
    });
  }
}
